import { z } from "zod";

import {
  authedServiceRequest,
  serviceRequest,
  serviceResponse,
} from "./BaseService";

/** ******************************************************************************
 *  Constants
 ******************************************************************************* */

// NOTE: This ID is ONLY used in the system table. Other tables which include
// the 'system' user use nano IDs for unique IDs.
export const SYSTEM_STATUS_ID = "system-status";

/** ******************************************************************************
 *  Types
 ******************************************************************************* */

export const SYSTEM_MODES = [
  "Operational",
  "Degraded",
  "Outage",
  "Maintenance",
] as const;

export const SystemModeSchema = z.enum(SYSTEM_MODES);

export const SystemStatusSchema = z.object({
  _id: z.string(),
  mode: SystemModeSchema,
  message: z.string(),
  allowTraffic: z.boolean(),
  lastUpdatedBy: z.string(),
  waitlistEnabled: z.boolean(),
  updatedAt: z.date(),
  createdAt: z.date(),
});

export type SystemStatus = z.infer<typeof SystemStatusSchema>;
export type SystemMode = z.infer<typeof SystemModeSchema>;

/** ******************************************************************************
 *  Get System Status
 ******************************************************************************* */

export const getSystemStatusParams = z.undefined();

export const getSystemStatusRequest = serviceRequest.merge(
  z.object({
    params: getSystemStatusParams,
  }),
);

export const getSystemStatusResponse = serviceResponse.merge(
  z.object({
    systemStatus: SystemStatusSchema.nullish(),
  }),
);

export type GetSystemStatusParams = z.infer<typeof getSystemStatusParams>;
export type GetSystemStatusRequest = z.infer<typeof getSystemStatusRequest>;
export type GetSystemStatusResponse = z.infer<typeof getSystemStatusResponse>;

/** ******************************************************************************
 *  Super Set System Status
 ******************************************************************************* */

export const superSetSystemStatusParams = z.object({
  mode: SystemModeSchema.optional(),
  message: z.string().optional(),
  allowTraffic: z.boolean().optional(),
  waitlistEnabled: z.boolean().optional(),
});

export const superSetSystemStatusRequest = authedServiceRequest.merge(
  z.object({
    params: superSetSystemStatusParams,
  }),
);

export const superSetSystemStatusResponse = serviceResponse.merge(
  z.object({
    systemStatus: SystemStatusSchema.nullish(),
  }),
);

export type SuperSetSystemStatusParams = z.infer<
  typeof superSetSystemStatusParams
>;
export type SuperSetSystemStatusRequest = z.infer<
  typeof superSetSystemStatusRequest
>;
export type SuperSetSystemStatusResponse = z.infer<
  typeof superSetSystemStatusResponse
>;

export type SystemServiceInterface = {
  get(request: GetSystemStatusRequest): Promise<GetSystemStatusResponse>;
  setSystemStatus(
    request: SuperSetSystemStatusRequest,
  ): Promise<SuperSetSystemStatusResponse>;
};
